import React, { useEffect, useMemo, useState, useCallback } from "react";
import { CheckCircle2 } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import { getAnomalies, resolveAnomaly } from "../payrollService";

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

export default function PayrollAnomalies() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");
  const [rows, setRows] = useState([]);
  const [actingId, setActingId] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      const data = await getAnomalies(companyId);
      setRows(data || []);
    } catch (err) {
      setMsg(err.message || "Failed to load anomalies");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCount = useMemo(
    () => rows.filter((x) => !x.resolved).length,
    [rows]
  );

  const highSeverity = useMemo(
    () => rows.filter((x) => x.severity === "high" && !x.resolved).length,
    [rows]
  );

  async function handleResolve(row) {
    try {
      setMsg("");
      setActingId(row.id);

      await resolveAnomaly(row.id, {
        resolved: true,
        resolved_at: new Date().toISOString(),
      });

      await load();
      setMsg("Anomaly marked as resolved.");
    } catch (err) {
      setMsg(err.message || "Failed to resolve anomaly");
    } finally {
      setActingId("");
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Total Anomalies</p>
          <h3>{rows.length}</h3>
          <span>All detected payroll issues</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Open Issues</p>
          <h3>{openCount}</h3>
          <span>Require attention</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">High Severity</p>
          <h3>{highSeverity}</h3>
          <span>Critical issues</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Resolved</p>
          <h3>{rows.filter((x) => x.resolved).length}</h3>
          <span>Issues already fixed</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Payroll Anomalies</h3>
            <p>
              Review and resolve payroll issues such as missing bank details,
              negative net pay, and configuration gaps.
            </p>
          </div>
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Type</th>
                <th>Severity</th>
                <th>Message</th>
                <th>Status</th>
                <th>Detected</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const employee = pickOne(row.payroll_employees);

                return (
                  <tr key={row.id}>
                    <td>
                      {employee
                        ? `${employee.employee_code} - ${employee.first_name} ${employee.last_name}`
                        : "-"}
                    </td>

                    <td>{row.anomaly_type}</td>

                    <td>
                      <span className={`pill ${row.severity}`}>
                        {row.severity}
                      </span>
                    </td>

                    <td>{row.anomaly_message}</td>

                    <td>
                      <span
                        className={`pill ${
                          row.resolved ? "approved" : "processing"
                        }`}
                      >
                        {row.resolved ? "resolved" : "open"}
                      </span>
                    </td>

                    <td>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString()
                        : "-"}
                    </td>

                    <td>
                      {!row.resolved ? (
                        <button
                          type="button"
                          className="primary-btn"
                          onClick={() => handleResolve(row)}
                          disabled={actingId === row.id}
                        >
                          <CheckCircle2 size={16} />
                          {actingId === row.id ? "Resolving..." : "Resolve"}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })}

              {!rows.length && !busy && (
                <tr>
                  <td colSpan="7">No anomalies found.</td>
                </tr>
              )}

              {busy && (
                <tr>
                  <td colSpan="7">Loading...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
